import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { authFetch } from '../../utils/authFetch';

// Import risksSlice action for cross-slice update
import { moveScenarioToRisks as addRiskToState } from './risksSlice';

// Import legacy action types for extraReducers bridge
import {
  FETCH_RISK_SCENARIOS_START,
  FETCH_RISK_SCENARIOS_SUCCESS,
  FETCH_RISK_SCENARIOS_FAILURE,
  REFINE_SCENARIO_START,
  REFINE_SCENARIO_SUCCESS,
  REFINE_SCENARIO_FAILURE,
  MOVE_SCENARIO_TO_RISKS,
  CLEAR_SCENARIOS,
  FETCH_SECTION_SPECIFIC_RISKS_START,
  FETCH_SECTION_SPECIFIC_RISKS_SUCCESS,
  FETCH_SECTION_SPECIFIC_RISKS_FAILURE,
  REFRESH_RISKS_SUCCESS,
  GENERATE_RISK_NARRATIVE_START,
  GENERATE_RISK_NARRATIVE_SUCCESS,
  GENERATE_RISK_NARRATIVE_FAILURE,
} from '../actions/actionTypes.js';

// ============================================================================
// Risk Scenarios Slice
// Manages transient scenario lists during risk generation/refinement
// ============================================================================

/**
 * State Shape:
 * {
 *   byAssessment: {
 *     [assessmentUuid]: {
 *       [riskArea]: {
 *         [sourceName]: Scenario[]
 *       }
 *     }
 *   },
 *   loading: boolean,
 *   error: string | null,
 *   narrativeGenerating: boolean,
 *   narrativeError: string | null,
 *   generatedRisks: { [sectionName]: Risk[] }
 * }
 *
 * Scenario object shape:
 * {
 *   scenarioId: string,
 *   scenarioText: string,
 *   variantText?: string,
 *   rationale?: string,
 *   generationMethod?: string,
 *   cataloguesUsed?: string[],
 *   retrievedContext?: any[]
 * }
 */

const initialState = {
  byAssessment: {},
  loading: false,
  error: null,
  narrativeGenerating: false,
  narrativeError: null,
  generatedRisks: {},
};

// Helper to ensure nested structure exists
const ensureStructure = (state, assessmentUuid, riskArea, sourceName) => {
  if (!state.byAssessment[assessmentUuid]) {
    state.byAssessment[assessmentUuid] = {};
  }
  if (!state.byAssessment[assessmentUuid][riskArea]) {
    state.byAssessment[assessmentUuid][riskArea] = {};
  }
  if (sourceName && !state.byAssessment[assessmentUuid][riskArea][sourceName]) {
    state.byAssessment[assessmentUuid][riskArea][sourceName] = [];
  }
};

// ============================================================================
// Async Thunks
// ============================================================================

/**
 * Fetch risk scenarios using basic generation
 */
export const fetchRiskScenarios = createAsyncThunk(
  'riskScenarios/fetchScenarios',
  async ({ assessmentUuid, card, modelNarratives }, { rejectWithValue }) => {
    try {
      const res = await authFetch('/.netlify/functions/generateRiskScenarios', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ card, modelNarratives }),
      });

      if (!res.ok) {
        throw new Error(await res.text());
      }

      const { scenarios } = await res.json();
      return {
        assessmentUuid,
        riskArea: card.section,
        sourceName: card.title,
        scenarios,
      };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

/**
 * Fetch risk scenarios using RAG with catalogues
 */
export const fetchRiskScenariosWithRAG = createAsyncThunk(
  'riskScenarios/fetchScenariosWithRAG',
  async (
    { assessmentUuid, card, modelNarratives, selectedCatalogues = [], useAllCatalogues = true },
    { rejectWithValue }
  ) => {
    try {
      const res = await authFetch('/.netlify/functions/generateScenariosWithRAG', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          card,
          modelNarratives,
          selectedCatalogues,
          useAllCatalogues,
        }),
      });

      if (!res.ok) {
        throw new Error(await res.text());
      }

      const { scenarios, method, cataloguesUsed, retrievedRisks } = await res.json();

      const enhancedScenarios = scenarios.map((scenario) => ({
        ...scenario,
        generationMethod: method,
        cataloguesUsed: cataloguesUsed || [],
        retrievedContext: retrievedRisks || [],
      }));

      return {
        assessmentUuid,
        riskArea: card.section,
        sourceName: card.title,
        scenarios: enhancedScenarios,
        cataloguesUsed,
        generationMethod: method,
      };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

/**
 * Refine a single scenario
 */
export const refineScenario = createAsyncThunk(
  'riskScenarios/refineScenario',
  async (
    {
      assessmentUuid,
      sectionName,
      category,
      originalName,
      originalRiskText,
      userInstruction,
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await authFetch('/.netlify/functions/refineScenario', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          assessmentUuid,
          sectionName,
          category,
          originalName,
          originalRiskText,
          userInstruction,
        }),
      });

      if (!res.ok) {
        throw new Error(await res.text());
      }

      const { refinedRisk, rationale } = await res.json();

      return {
        assessmentUuid,
        riskArea: sectionName,
        sourceName: category,
        scenario: {
          scenarioId: originalName,
          scenarioText: refinedRisk,
          rationale,
        },
      };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

/**
 * Promote scenario to saved risk - saves to DB and updates both slices
 * Replaces legacy promoteScenarioToSaved from riskScenarioActions.js
 */
export const promoteScenarioToSaved = createAsyncThunk(
  'riskScenarios/promoteToSaved',
  async (
    { modelId, assessmentUuid, riskArea, sourceName, scenario, owner },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const riskId = scenario.scenarioId || uuidv4();

      const payload = {
        riskId,
        name: scenario.scenarioText || scenario.variantText || '',
        section: riskArea,
        category: sourceName,
        type: 'Scenario',
        rag_source_file: null,
        modelUuid: modelId,
        assessmentUuid,
        owner,
      };

      const response = await authFetch('/.netlify/functions/saveRisk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      console.log('[promoteScenarioToSaved] saveRisk successful:', result);

      // Update risksSlice with the new saved risk
      dispatch(
        addRiskToState({
          assessmentUuid,
          riskArea,
          sourceName,
          scenario: {
            ...scenario,
            riskId: result.riskId || riskId,
          },
          owner,
        })
      );

      // Return payload for this slice's fulfilled handler
      return {
        assessmentUuid,
        riskArea,
        sourceName,
        riskId: result.riskId || riskId,
      };
    } catch (err) {
      console.error('[promoteScenarioToSaved] saveRisk failed:', err);
      return rejectWithValue(err.message);
    }
  }
);

const riskScenariosSlice = createSlice({
  name: 'riskScenarioItems',
  initialState,
  reducers: {
    // RTK action creators for new code

    addScenarios: (state, action) => {
      const { assessmentUuid, riskArea, sourceName, scenarios } = action.payload;
      ensureStructure(state, assessmentUuid, riskArea, sourceName);
      state.byAssessment[assessmentUuid][riskArea][sourceName] = scenarios;
    },

    updateScenario: (state, action) => {
      const { assessmentUuid, riskArea, sourceName, scenario } = action.payload;
      const scenarios = state.byAssessment[assessmentUuid]?.[riskArea]?.[sourceName];
      if (!scenarios) return;

      const idx = scenarios.findIndex((s) => s.scenarioId === scenario.scenarioId);
      if (idx !== -1) {
        scenarios[idx] = { ...scenarios[idx], ...scenario };
      }
    },

    clearScenarios: (state, action) => {
      const { assessmentUuid, riskArea, sourceName } = action.payload;
      const area = state.byAssessment[assessmentUuid]?.[riskArea];
      if (!area) return;

      delete area[sourceName];

      // Cleanup empty structures
      if (Object.keys(area).length === 0) {
        delete state.byAssessment[assessmentUuid][riskArea];
      }
      if (Object.keys(state.byAssessment[assessmentUuid] || {}).length === 0) {
        delete state.byAssessment[assessmentUuid];
      }
    },

    clearAllScenariosForAssessment: (state, action) => {
      const { assessmentUuid } = action.payload;
      delete state.byAssessment[assessmentUuid];
    },

    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    setError: (state, action) => {
      state.error = action.payload;
    },

    // For generated risks from RAG
    setGeneratedRisks: (state, action) => {
      const { sectionName, risks } = action.payload;
      state.generatedRisks[sectionName] = risks;
    },

    clearGeneratedRisks: (state, action) => {
      const { sectionName } = action.payload;
      delete state.generatedRisks[sectionName];
    },
  },

  extraReducers: (builder) => {
    // ============================================================================
    // RTK Async Thunk handlers
    // ============================================================================

    builder
      .addCase(fetchRiskScenarios.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRiskScenarios.fulfilled, (state, action) => {
        const { assessmentUuid, riskArea, sourceName, scenarios } = action.payload;
        ensureStructure(state, assessmentUuid, riskArea, sourceName);
        state.byAssessment[assessmentUuid][riskArea][sourceName] = scenarios;
        state.loading = false;
      })
      .addCase(fetchRiskScenarios.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchRiskScenariosWithRAG.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRiskScenariosWithRAG.fulfilled, (state, action) => {
        const { assessmentUuid, riskArea, sourceName, scenarios } = action.payload;
        ensureStructure(state, assessmentUuid, riskArea, sourceName);
        state.byAssessment[assessmentUuid][riskArea][sourceName] = scenarios;
        state.loading = false;
      })
      .addCase(fetchRiskScenariosWithRAG.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(refineScenario.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refineScenario.fulfilled, (state, action) => {
        const { assessmentUuid, riskArea, sourceName, scenario } = action.payload;
        const scenarios = state.byAssessment[assessmentUuid]?.[riskArea]?.[sourceName];
        if (scenarios) {
          const idx = scenarios.findIndex((s) => s.scenarioId === scenario.scenarioId);
          if (idx !== -1) {
            scenarios[idx] = scenario;
          }
        }
        state.loading = false;
      })
      .addCase(refineScenario.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // promoteScenarioToSaved - clears scenario from this slice after save
    builder
      .addCase(promoteScenarioToSaved.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(promoteScenarioToSaved.fulfilled, (state, action) => {
        const { assessmentUuid, riskArea, sourceName } = action.payload;
        // Clear the scenario from this slice (risk added to risksSlice by thunk)
        const area = state.byAssessment[assessmentUuid]?.[riskArea];
        if (area) {
          delete area[sourceName];
          if (Object.keys(area).length === 0) {
            delete state.byAssessment[assessmentUuid][riskArea];
          }
          if (Object.keys(state.byAssessment[assessmentUuid] || {}).length === 0) {
            delete state.byAssessment[assessmentUuid];
          }
        }
        state.loading = false;
      })
      .addCase(promoteScenarioToSaved.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ============================================================================
    // Legacy Action Type Bridge
    // ============================================================================

    builder
      .addCase(FETCH_RISK_SCENARIOS_START, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(FETCH_RISK_SCENARIOS_SUCCESS, (state, action) => {
        // Legacy uses modelId, we normalize to assessmentUuid (prefer assessmentUuid if both present)
        const { modelId, assessmentUuid: payloadAssessmentUuid, riskArea, sourceName, scenarios } = action.payload;
        const key = payloadAssessmentUuid || modelId; // Prefer assessmentUuid, fallback to modelId
        ensureStructure(state, key, riskArea, sourceName);
        state.byAssessment[key][riskArea][sourceName] = scenarios;
        state.loading = false;
      })
      .addCase(FETCH_RISK_SCENARIOS_FAILURE, (state, action) => {
        state.loading = false;
        state.error = action.payload.error;
      });

    builder
      .addCase(REFINE_SCENARIO_START, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(REFINE_SCENARIO_SUCCESS, (state, action) => {
        const { modelId, assessmentUuid: payloadAssessmentUuid, riskArea, sourceName, scenario } = action.payload;
        const key = payloadAssessmentUuid || modelId; // Prefer assessmentUuid, fallback to modelId
        const scenarios = state.byAssessment[key]?.[riskArea]?.[sourceName];
        if (scenarios) {
          const idx = scenarios.findIndex((s) => s.scenarioId === scenario.scenarioId);
          if (idx !== -1) {
            scenarios[idx] = scenario;
          }
        }
        state.loading = false;
      })
      .addCase(REFINE_SCENARIO_FAILURE, (state, action) => {
        state.loading = false;
        state.error = action.payload.error;
      });

    // MOVE_SCENARIO_TO_RISKS and CLEAR_SCENARIOS remove scenarios from state
    builder.addCase(MOVE_SCENARIO_TO_RISKS, (state, action) => {
      const { assessmentUuid, modelId, riskArea, sourceName } = action.payload;
      const key = assessmentUuid || modelId;
      const area = state.byAssessment[key]?.[riskArea];
      if (area) {
        delete area[sourceName];
        if (Object.keys(area).length === 0) {
          delete state.byAssessment[key][riskArea];
        }
      }
    });

    builder.addCase(CLEAR_SCENARIOS, (state, action) => {
      const { modelId, assessmentUuid, riskArea, sourceName } = action.payload;
      const key = assessmentUuid || modelId; // Prefer assessmentUuid, fallback to modelId
      const area = state.byAssessment[key]?.[riskArea];
      if (area) {
        delete area[sourceName];
        if (Object.keys(area).length === 0) {
          delete state.byAssessment[key][riskArea];
        }
      }
    });

    // Section-specific risks generation (from RAG)
    builder
      .addCase(FETCH_SECTION_SPECIFIC_RISKS_START, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(FETCH_SECTION_SPECIFIC_RISKS_SUCCESS, (state, action) => {
        const { sectionName, risks } = action.payload;
        state.loading = false;
        state.generatedRisks[sectionName] = risks;
      })
      .addCase(FETCH_SECTION_SPECIFIC_RISKS_FAILURE, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error;
      });

    // Refresh risks
    builder.addCase(REFRESH_RISKS_SUCCESS, (state, action) => {
      const { sectionName, risks } = action.payload;
      state.generatedRisks[sectionName] = risks;
    });

    // Risk narrative generation
    builder
      .addCase(GENERATE_RISK_NARRATIVE_START, (state) => {
        state.narrativeGenerating = true;
        state.narrativeError = null;
      })
      .addCase(GENERATE_RISK_NARRATIVE_SUCCESS, (state) => {
        state.narrativeGenerating = false;
      })
      .addCase(GENERATE_RISK_NARRATIVE_FAILURE, (state, action) => {
        state.narrativeGenerating = false;
        state.narrativeError = action.payload?.error;
      });
  },
});

export const {
  addScenarios,
  updateScenario,
  clearScenarios,
  clearAllScenariosForAssessment,
  setLoading,
  setError,
  setGeneratedRisks,
  clearGeneratedRisks,
} = riskScenariosSlice.actions;

// ============================================================================
// Selectors
// ============================================================================

export const selectScenariosByAssessment = (state) => state.riskScenarioItems.byAssessment;

export const selectScenariosForSource = (state, assessmentUuid, riskArea, sourceName) =>
  state.riskScenarioItems.byAssessment[assessmentUuid]?.[riskArea]?.[sourceName] || [];

export const selectScenariosForArea = (state, assessmentUuid, riskArea) =>
  state.riskScenarioItems.byAssessment[assessmentUuid]?.[riskArea] || {};

export const selectAllScenariosForAssessment = (state, assessmentUuid) => {
  const areas = state.riskScenarioItems.byAssessment[assessmentUuid] || {};
  const allScenarios = [];

  Object.keys(areas).forEach((riskArea) => {
    const sources = areas[riskArea] || {};
    Object.keys(sources).forEach((sourceName) => {
      allScenarios.push(
        ...sources[sourceName].map((s) => ({
          ...s,
          riskArea,
          sourceName,
        }))
      );
    });
  });

  return allScenarios;
};

export const selectScenariosLoading = (state) => state.riskScenarioItems.loading;
export const selectScenariosError = (state) => state.riskScenarioItems.error;
export const selectNarrativeGenerating = (state) => state.riskScenarioItems.narrativeGenerating;
export const selectGeneratedRisks = (state) => state.riskScenarioItems.generatedRisks;

export const selectGeneratedRisksForSection = (state, sectionName) =>
  state.riskScenarioItems.generatedRisks[sectionName] || [];

/**
 * Count total scenarios across all sources for an assessment
 */
export const selectScenarioCount = (state, assessmentUuid) =>
  selectAllScenariosForAssessment(state, assessmentUuid).length;

export default riskScenariosSlice.reducer;
